import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { IOptionType } from 'interfaces/forms';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import {
  IProjectedCashFlowValuesType,
  PROJECTED_CASH_FLOW_OPTION_NAMES,
  projectedCashFlowOptionsToComponentMap,
  projectedCashFlowPrefix,
  selectedTabPrefix,
  useGetInputFieldsList,
  useGetProjectedCashFlowOptions,
} from './utils/form';
import { FinancialPlanChipSelector } from '../components/FinancialPlanChipSelector';
import { ActiveTabContextProvider, useTabChangeContext } from '../hooks/useTabChangeContext';
import { isEqualFieldName } from '../OpeningBalanceSheets/utils/form';

import styles from './ProjectedCashFlow.module.scss';

const ProjectedCashFlowContent: FC = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const PROJECTED_CASH_FLOW_OPTIONS = useGetProjectedCashFlowOptions();
  const INPUT_FIELDS_LIST = useGetInputFieldsList();

  const { activeTab, onTabChange } = useTabChangeContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PROJECTED_CASH_FLOW,
  );

  const Component = projectedCashFlowOptionsToComponentMap[activeTab.value as IProjectedCashFlowValuesType];

  const onTabChangeHandler = (option: IOptionType[]) => {
    setValue(selectedTabPrefix, option[0].value);
    onTabChange(option);
  };
  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE} disabled={!canAccess}>
        <FormHeader
          locked={!canAccess}
          title={t('financialPlan.title')}
          badgeText={t('financialPlan.projectedCashFlow.title')}
        />
      </FormSectionCard>

      <FormSectionCard collapsible title={t('financialPlan.projectedCashFlow.cashFlows')} disabled={!canAccess}>
        <Box className={styles.contentWrapper}>
          <FinancialPlanChipSelector
            wrapWithSectionCard={false}
            options={PROJECTED_CASH_FLOW_OPTIONS}
            selected={activeTab}
            onChange={onTabChangeHandler}
          />

          <Component />
        </Box>
      </FormSectionCard>

      {INPUT_FIELDS_LIST.map(({ title, name }) => {
        return (
          <FormSectionCard
            disabled={!canAccess}
            title={title}
            key={title}
            aiFieldName={concatWithDot(projectedCashFlowPrefix, name)}
          >
            <TextInputControl
              name={concatWithDot(projectedCashFlowPrefix, name)}
              control={control}
              multiline
              minRows={3}
              maxLength={1500}
              disabled={!canAccess}
            />
          </FormSectionCard>
        );
      })}
    </>
  );
};

export const ProjectedCashFlow: FC = () => {
  const { getValues } = useFormContext();
  const PROJECTED_CASH_FLOW_OPTIONS = useGetProjectedCashFlowOptions();

  const selectedType = getValues(selectedTabPrefix);
  const isMatch = getValues(isEqualFieldName);
  const initialValue =
    !isMatch && selectedType === PROJECTED_CASH_FLOW_OPTION_NAMES.IN_DIRECT
      ? PROJECTED_CASH_FLOW_OPTIONS[0]
      : (PROJECTED_CASH_FLOW_OPTIONS.find((option) => option.value === selectedType) ?? PROJECTED_CASH_FLOW_OPTIONS[0]);

  return (
    <ActiveTabContextProvider initialValue={initialValue}>
      <ProjectedCashFlowContent />
    </ActiveTabContextProvider>
  );
};
